/* eslint-disable unicorn/prevent-abbreviations -- widely imported as utils.ts, renaming would break many imports */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs));
}
export function formatFileSize(bytes: number): string {
	if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
	if (bytes === 0) return '0 B';

	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB'];
	const index = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);

	return `${Number.parseFloat((bytes / Math.pow(k, index)).toFixed(1))} ${sizes[index]}`;
}
export function formatRelativeTime(timestamp: number): string {
	const now = Date.now();
	const diff = now - timestamp;

	const seconds = Math.floor(diff / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (days > 0) return `${days}d ago`;
	if (hours > 0) return `${hours}h ago`;
	if (minutes > 0) return `${minutes}m ago`;
	return 'just now';
}
export function debounce<T extends (...arguments_: unknown[]) => unknown>(
	function_: T,
	delay: number,
): (...arguments_: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout>;

	return (...arguments_: Parameters<T>) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => function_(...arguments_), delay);
	};
}
export function throttle<T extends (...arguments_: unknown[]) => unknown>(
	function_: T,
	delay: number,
): (...arguments_: Parameters<T>) => void {
	let lastCall = 0;

	return (...arguments_: Parameters<T>) => {
		const now = Date.now();
		if (now - lastCall >= delay) {
			lastCall = now;
			function_(...arguments_);
		}
	};
}
export function getFileExtension(path: string): string {
	const match = path.match(/\.([^./]+)$/);
	return match ? match[1].toLowerCase() : '';
}
export function getFilename(path: string): string {
	const parts = path.split('/');
	return parts.at(-1) || path;
}
export function getDirectory(path: string): string {
	const lastSlash = path.lastIndexOf('/');
	return lastSlash > 0 ? path.slice(0, lastSlash) : '/';
}
export function isDirectory(path: string): boolean {
	return path.endsWith('/');
}
export function normalizePath(path: string): string {
	return '/' + path.replace(/^\/+/, '').replaceAll(/\/+/g, '/');
}

/**
 * Check whether an error indicates a network/connection failure.
 *
 * `fetch` throws a `TypeError` when the request cannot be sent at all
 * (offline, DNS failure, CORS pre-flight failure, etc.). This helper
 * detects that case so callers can show a user-friendly offline message.
 */
export function isNetworkError(error: unknown): boolean {
	return error instanceof TypeError && (error.message === 'Failed to fetch' || error.message === 'Load failed');
}
